/**
 * Full-page error component.
 *
 * Reusable component for displaying full-page errors with consistent styling,
 * branding, and action buttons. Used by error.tsx, global-error.tsx, and other
 * error pages.
 */

import { ContentSection } from 'sidecar-components/atoms/ContentSection'
import { Icon, IconSize, type IconProps } from 'sidecar-components/atoms/Icon'
import { Button, ButtonSize } from 'sidecar-components/atoms/Button'

/** Action button configuration. */
export interface ErrorAction {
  label: string
  icon?: IconProps['name']
  onClick?: () => void
  href?: string
  testId?: string
}

/** Props for the ErrorPage component. */
export interface ErrorPageProps {
  title: string
  message: string
  code?: string
  icon?: IconProps['name']
  actions?: ErrorAction[]
  className?: string
}

/**
 * ErrorPage component for full-page error displays.
 *
 * Provides a consistent, branded error page layout with customizable title,
 * message, icon, and action buttons.
 */
export function ErrorPage({
  title,
  message,
  code,
  icon = 'sidecar',
  actions = [],
  className,
}: ErrorPageProps) {
  return (
    <ContentSection breathable className={`text-center ${className || ''}`}>
      {/* Brand icon */}
      <Icon
        name={icon}
        className="mx-auto color-iconBrand-default"
        size={IconSize.XXXXL}
      />

      {/* Error title */}
      <h1 className="my-12">{title}</h1>

      {/* Error message */}
      <p className="color-text-weak mb-8">{message}</p>

      {/* Error code for support reference */}
      {code && (
        <p className="text-sm color-text-weak mb-8">Error code: {code}</p>
      )}

      {/* Action buttons */}
      {actions.length > 0 && (
        <div className="flex gap-4 justify-center flex-wrap">
          {actions.map((action, index) => (
            <ErrorActionButton key={index} {...action} />
          ))}
        </div>
      )}
    </ContentSection>
  )
}

/**
 * Error action button component (internal).
 *
 * Renders either a button with onClick or a link button with href.
 */
function ErrorActionButton({
  label,
  icon,
  onClick,
  href,
  testId,
}: ErrorAction) {
  // Link button
  if (href) {
    return (
      <a href={href}>
        <Button size={ButtonSize.LARGE} icon={icon as string} testId={testId}>
          {label}
        </Button>
      </a>
    )
  }

  // Click button
  return (
    <Button
      size={ButtonSize.LARGE}
      icon={icon as string}
      onClick={onClick}
      testId={testId}
    >
      {label}
    </Button>
  )
}
